import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LOGO, STATUS_STYLES, fmtDate } from "@/lib/helpers";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
  LayoutDashboard, Users, ClipboardList, CalendarCheck, ShieldCheck,
  Trophy, FileSpreadsheet, FileBarChart, Crown, ArrowRight, CheckCircle2, Calendar, Target, FileDown, CreditCard, ReceiptText, Award,
} from "lucide-react";

const CARDS = [
  { to: "/dashboard", label: "Executive Dashboard", desc: "Live KPIs, charts & completion metrics", icon: LayoutDashboard, roles: ["founder", "admin", "employee"] },
  { to: "/management", label: "Management Overview", desc: "Founder-level org health & rankings", icon: Crown, roles: ["founder", "admin"] },
  { to: "/employees", label: "Employee Master", desc: "Team directory & records", icon: Users, roles: ["founder", "admin"] },
  { to: "/id-cards", label: "ID Cards", desc: "Print & download employee ID cards", icon: CreditCard, roles: ["founder", "admin", "employee"] },
  { to: "/certificates", label: "Welcome Certificate", desc: "Branded welcome certificates", icon: Award, roles: ["founder", "admin", "employee"] },
  { to: "/tasks", label: "Task Allocation", desc: "Assign & track daily tasks", icon: ClipboardList, roles: ["founder", "admin", "employee"] },
  { to: "/invoices", label: "Invoice Generator", desc: "Original, Duplicate & Triplicate copies", icon: ReceiptText, roles: ["founder", "admin", "employee"] },
  { to: "/attendance", label: "Attendance", desc: "Login/logout & working hours", icon: CalendarCheck, roles: ["founder", "admin", "employee"] },
  { to: "/quality", label: "Quality Check", desc: "Accuracy, errors & rework", icon: ShieldCheck, roles: ["founder", "admin", "employee"] },
  { to: "/performance", label: "Performance", desc: "Leaderboard & rankings", icon: Trophy, roles: ["founder", "admin", "employee"] },
  { to: "/workspace", label: "Employee Workspace", desc: "Work logs & end-of-day summary", icon: FileSpreadsheet, roles: ["founder", "admin", "employee"] },
  { to: "/reports", label: "Reports & Export", desc: "Weekly/monthly reports, print & export", icon: FileBarChart, roles: ["founder", "admin", "employee"] },
];

export default function Home() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);
  const cards = CARDS.filter((c) => c.roles.includes(user?.role || "employee"));
  const hour = now.getHours();
  const greet = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";

  const isEmployee = (user?.role || "employee") === "employee";
  const { data: tasks = [], isLoading: tasksLoading } = useQuery({
    queryKey: ["home-tasks"],
    queryFn: async () => (await api.get("/tasks")).data,
    enabled: isEmployee,
  });
  const activeTasks = tasks.filter((t) => t.status !== "Completed");

  const downloadSource = async (importId, name) => {
    try {
      const res = await api.get(`/tasks/import-file/${importId}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = name || "tasks.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download the Excel file"); }
  };

  return (
    <div data-testid="home-page">
      <Card className="relative overflow-hidden rounded-lg border-slate-200 bg-gold text-navy-900 mb-8">
        <div
          className="absolute inset-0 opacity-15"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1462556791646-c201b8241a94?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400)",
            backgroundSize: "cover", backgroundPosition: "center",
          }}
        />
        <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center gap-6 p-8">
          <img src={LOGO} alt="VNS" className="h-20 w-20 object-contain shrink-0 bg-white rounded-lg p-1" />
          <div className="flex-1">
            <p className="text-navy-900 text-sm font-medium">{greet}, {user?.name?.split(" ")[0]}</p>
            <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight mt-1">
              VNS Vinayaka Group of Enterprises
            </h1>
            <p className="text-navy/70 text-sm mt-1">Data Entry Division · Operations Management System</p>
          </div>
          <div className="text-right">
            <div className="font-heading text-3xl font-extrabold text-navy-900 tracking-tight">
              {now.toLocaleTimeString("en-GB")}
            </div>
            <div className="text-xs text-navy/70">
              {now.toLocaleDateString("en-GB", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}
            </div>
          </div>
        </div>
      </Card>

      {isEmployee && (
        <div className="mb-8" data-testid="my-tasks-panel">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg font-semibold text-navy tracking-tight flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-gold-dark" /> My Assigned Tasks
              {activeTasks.length > 0 && (
                <Badge className="bg-navy text-gold" data-testid="my-tasks-count">{activeTasks.length}</Badge>
              )}
            </h2>
            <Button
              variant="outline"
              size="sm"
              data-testid="view-all-tasks-btn"
              onClick={() => navigate("/tasks")}
              className="rounded-lg border-navy/20 text-navy hover:bg-navy hover:text-white"
            >
              View all <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </div>

          {tasksLoading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3].map((i) => <Skeleton key={i} className="h-40 rounded-lg" />)}
            </div>
          ) : activeTasks.length === 0 ? (
            <Card className="rounded-lg border-slate-200 bg-white p-8 text-center" data-testid="my-tasks-empty">
              <CheckCircle2 className="mx-auto h-10 w-10 text-emerald-500 mb-2" />
              <p className="font-heading font-semibold text-navy">You're all caught up!</p>
              <p className="text-sm text-slate-500 mt-1">No pending tasks assigned to you right now.</p>
            </Card>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {activeTasks.map((t) => (
                <Card
                  key={t.task_id}
                  data-testid={`my-task-card-${t.task_id}`}
                  onClick={() => navigate("/tasks")}
                  className={`group cursor-pointer rounded-lg border p-5 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 ${t.overdue ? "border-red-300 bg-red-50/60" : "border-slate-200 bg-white"}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-heading font-semibold text-navy tracking-tight">{t.project || "Task"}</h3>
                    <Badge variant="outline" className={STATUS_STYLES[t.priority]}>{t.priority}</Badge>
                  </div>
                  <p className="mt-2 text-sm text-slate-600 whitespace-pre-line line-clamp-4">{t.description || "No description provided."}</p>
                  <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
                    <span className={`inline-flex items-center gap-1 ${t.overdue ? "text-red-700 font-semibold" : ""}`}>
                      <Calendar className="h-3.5 w-3.5" /> {fmtDate(t.deadline)}{t.overdue && " · Overdue"}
                    </span>
                    <span className="inline-flex items-center gap-1"><Target className="h-3.5 w-3.5" /> Target: {t.target}</span>
                  </div>
                  <div className="mt-3 flex items-center justify-between gap-2">
                    <Badge variant="outline" className={STATUS_STYLES[t.status]}>{t.status}</Badge>
                    {t.import_id && (
                      <Button
                        variant="outline"
                        size="sm"
                        data-testid={`download-excel-${t.task_id}`}
                        onClick={(e) => { e.stopPropagation(); downloadSource(t.import_id, t.source_file); }}
                        className="rounded-lg border-emerald-200 text-emerald-700 hover:bg-emerald-50 h-8 text-xs"
                      >
                        <FileDown className="mr-1 h-3.5 w-3.5" /> View Excel
                      </Button>
                    )}
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      <h2 className="font-heading text-lg font-semibold text-navy mb-4 tracking-tight">Quick Navigation</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {cards.map((c) => (
          <Card
            key={c.to}
            data-testid={`home-card-${c.to.slice(1)}`}
            onClick={() => navigate(c.to)}
            className="group cursor-pointer rounded-lg border-slate-200 bg-white p-6 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
          >
            <div className="flex items-start justify-between">
              <div className="rounded-lg bg-navy/5 p-3 group-hover:bg-gold/20 transition-colors">
                <c.icon className="h-6 w-6 text-navy" />
              </div>
              <ArrowRight className="h-4 w-4 text-slate-300 group-hover:text-gold group-hover:translate-x-1 transition-all" />
            </div>
            <h3 className="mt-4 font-heading font-semibold text-navy tracking-tight">{c.label}</h3>
            <p className="mt-1 text-sm text-slate-500">{c.desc}</p>
          </Card>
        ))}
      </div>
    </div>
  );
}
